(() => {
  const initializedHomes = new WeakSet();
  const cleanupByHome = new WeakMap();

  const emit = (name, detail = {}) => {
    const payload = { ...detail, path: window.location.pathname };
    document.dispatchEvent(new CustomEvent(name, { detail: payload }));

    try {
      window.Shopify?.analytics?.publishCustomEvent?.(
        `field_signal:${name.replace('fieldsignal:', '')}`,
        payload
      );
    } catch (_) {
      // Analytics is optional and must never interrupt navigation.
    }
  };

  const initialize = (home) => {
    if (!home || initializedHomes.has(home)) return;
    initializedHomes.add(home);
    const cleanups = [];
    document.body.classList.add('fs-field-home-active');

    home.querySelectorAll('[data-fs-home-cta]').forEach((link) => {
      link.addEventListener('click', () => {
        emit('fieldsignal:offer_jump', {
          placement: link.dataset.fsHomeCta || 'homepage',
          destination: link.getAttribute('href') || '',
        });
      });
    });

    home.querySelectorAll('.fs-home-faq details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        emit('fieldsignal:faq_toggle', {
          question: details.querySelector('summary')?.textContent?.trim() || '',
          open: true,
        });
      });
    });

    const reviewRail = home.querySelector('.fs-home-reviews__grid');
    const previousReview = home.querySelector('[data-fs-review-prev]');
    const nextReview = home.querySelector('[data-fs-review-next]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const moveReviewRail = (direction) => {
      if (!reviewRail) return;
      reviewRail.scrollBy({
        left: direction * Math.max(300, reviewRail.clientWidth * 0.78),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
      emit('fieldsignal:gallery_interaction', {
        gallery: 'homepage_testimonials',
        direction: direction > 0 ? 'next' : 'previous',
      });
    };

    const updateReviewControls = () => {
      if (!reviewRail) return;
      const maximumScroll = Math.max(0, reviewRail.scrollWidth - reviewRail.clientWidth);
      if (previousReview) previousReview.disabled = reviewRail.scrollLeft <= 2;
      if (nextReview) nextReview.disabled = reviewRail.scrollLeft >= maximumScroll - 2;
    };

    if (reviewRail) {
      previousReview?.addEventListener('click', () => moveReviewRail(-1));
      nextReview?.addEventListener('click', () => moveReviewRail(1));
      reviewRail.addEventListener('scroll', updateReviewControls, { passive: true });
      window.addEventListener('resize', updateReviewControls, { passive: true });
      cleanups.push(() => window.removeEventListener('resize', updateReviewControls));
      updateReviewControls();
    }

    const signalTrack = home.querySelector('[data-fs-signal-track]');
    const signalToggle = home.querySelector('[data-fs-signal-toggle]');
    const signalIcon = signalToggle?.querySelector('[data-fs-signal-icon]');

    signalToggle?.addEventListener('click', () => {
      if (!signalTrack) return;
      const paused = signalTrack.classList.toggle('is-paused');
      const pauseLabel = signalToggle.dataset.fsSignalPauseLabel || 'Pause scrolling publication logos';
      const resumeLabel = signalToggle.dataset.fsSignalResumeLabel || 'Resume scrolling publication logos';
      signalToggle.setAttribute('aria-pressed', String(paused));
      signalToggle.setAttribute('aria-label', paused ? resumeLabel : pauseLabel);
      if (signalIcon) signalIcon.textContent = paused ? '▶' : 'Ⅱ';
      emit('fieldsignal:gallery_interaction', {
        gallery: 'homepage_supplier_publication_logos',
        action: paused ? 'pause' : 'resume',
      });
    });

    const sticky = home.querySelector('[data-fs-home-sticky]');
    const footer = home.querySelector('.fs-home-footer');
    if (sticky && footer && 'IntersectionObserver' in window) {
      const footerObserver = new IntersectionObserver(
        ([entry]) => sticky.classList.toggle('is-hidden', entry.isIntersecting),
        { threshold: 0.06 }
      );
      footerObserver.observe(footer);
      cleanups.push(() => footerObserver.disconnect());
    }

    cleanupByHome.set(home, () => cleanups.forEach((cleanup) => cleanup()));
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll('[data-fs-field-home]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-fs-field-home]').forEach((home) => {
      cleanupByHome.get(home)?.();
      cleanupByHome.delete(home);
      initializedHomes.delete(home);
    });

    queueMicrotask(() => {
      if (!document.querySelector('[data-fs-field-home]')) {
        document.body.classList.remove('fs-field-home-active');
      }
    });
  });
})();
